import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LockKeyIcon, PaperPlaneTiltIcon, SpinnerGapIcon, CheckCircleIcon, CoinsIcon } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { fetchHeliusAccess, requestHeliusAccess } from "../lib/api";

export const HeliusGate = ({ feature = "This page", children }) => {
  const { user, openAuth } = useAuth();
  const qc = useQueryClient();
  const [sending, setSending] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["heliusAccess", user?.id || "anon"],
    queryFn: fetchHeliusAccess,
    staleTime: 60000,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-24" data-testid="helius-gate-loading">
        <SpinnerGapIcon size={32} className="animate-spin text-neon" />
      </div>
    );
  }
  if (data?.access) return children;

  const request = async () => {
    setSending(true);
    try {
      await requestHeliusAccess();
      toast.success("Request sent — the admin will review it");
      qc.invalidateQueries({ queryKey: ["heliusAccess"] });
    } catch {
      toast.error("Couldn't send the request — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center space-y-6 animate-fade-up" data-testid="helius-gate-lock">
      <div className="w-16 h-16 mx-auto flex items-center justify-center border border-neon/40 bg-neon/5">
        <LockKeyIcon size={32} className="text-neon" />
      </div>
      <h1 className="font-heading font-black text-3xl tracking-tight">{feature} runs on Helius credits</h1>
      <div className="text-sm text-muted-foreground leading-relaxed space-y-3 text-left border border-white/10 p-5">
        <p>
          AlphaApeing is free, but the live on-chain wallet data behind this page comes from a paid
          Helius plan that the admin covers out of pocket every month.
        </p>
        <p>
          The credit pool is shared by everyone and refreshes on <span className="text-white font-semibold">{data?.resetsOn || "the 10th"}</span>.
          Once it runs dry, these features go dark for <em>all</em> of us until the reset — so access is
          granted person-by-person to make the credits last.
        </p>
        <p className="flex items-start gap-2 text-xs">
          <CoinsIcon size={16} className="text-gold shrink-0 mt-0.5" />
          <span>If you're approved, please use it thoughtfully — every wallet scan and PnL lookup spends
          from the same pool. Wasted credits shorten the month for everybody.</span>
        </p>
      </div>
      {!data?.signedIn ? (
        <button
          data-testid="helius-gate-signin-btn"
          onClick={() => openAuth("login", "", "request Helius access")}
          className="px-6 py-3 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors"
        >
          Sign in to request access
        </button>
      ) : data?.requested ? (
        <div className="inline-flex items-center gap-2 px-5 py-3 border border-neon/40 text-neon text-sm font-semibold" data-testid="helius-request-pending">
          <CheckCircleIcon size={18} /> Request sent — the admin will review it soon
        </div>
      ) : (
        <button
          data-testid="helius-request-btn"
          onClick={request}
          disabled={sending}
          className="inline-flex items-center gap-2 px-6 py-3 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors disabled:opacity-60"
        >
          {sending ? <SpinnerGapIcon size={18} className="animate-spin" /> : <PaperPlaneTiltIcon size={18} />}
          Request access
        </button>
      )}
    </div>
  );
};

export default HeliusGate;
